import React, { useEffect, useState } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  IconButton,
  Typography,
} from '@mui/material';

import SearchItem from './components/search/search-item';
import SearchNode from './components/search/search-node';
import { NodeDisplayGroup } from './config/node-display-group';
import {
  commandNodeStyles,
  connectorNodeStyles,
  dataframeNodeStyles,
  formatNodeStyles,
  taskNodeStyles,
} from './nodes/styles';

interface SidebarNode {
  type: string;
  title: string;
  node: string;
}

const nodeColorsMap = {
  connector: connectorNodeStyles.background,
  command: commandNodeStyles.background,
  dataframes: dataframeNodeStyles.background,
  format: formatNodeStyles.background,
  task: taskNodeStyles.background,
};

interface SidebarProps {
  onCollapseChange?: (collapsed: boolean) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ onCollapseChange }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    if (onCollapseChange) {
      onCollapseChange(newCollapsedState);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {isCollapsed ? (
        <Box sx={{ p: 1 }}>
          <IconButton onClick={toggleSidebar} size="small">
            &gt;
          </IconButton>
        </Box>
      ) : (
        <>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}
          >
            <Typography variant="h6">Toolbox</Typography>
            <IconButton onClick={toggleSidebar} size="small">
              &lt;
            </IconButton>
          </Box>
          <Typography variant="body2" sx={{ px: 2, pb: 2 }}>
            Drag nodes onto the canvas to build your recipe
          </Typography>
          <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ color: nodeColorsMap.connector, fontSize: 24 }}>●</Box>
                  <Typography>Connectors</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <SearchNode nodeType="connector" bgColor={nodeColorsMap.connector} />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ color: nodeColorsMap.command, fontSize: 24 }}>●</Box>
                  <Typography>Commands</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <ul className="node-list">
                  {NodeDisplayGroup.commands.map((node: SidebarNode) => (
                    <SearchItem
                      key={node.type}
                      title={node.title}
                      type={node.type}
                      bgColor={nodeColorsMap.command}
                    />
                  ))}
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ color: nodeColorsMap.dataframes, fontSize: 24 }}>●</Box>
                  <Typography>Data Frames</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <ul className="node-list">
                  {NodeDisplayGroup.dataframes.map((node: SidebarNode) => (
                    <SearchItem
                      key={node.type}
                      title={node.title}
                      type={node.type}
                      bgColor={nodeColorsMap.dataframes}
                    />
                  ))}
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ color: nodeColorsMap.format, fontSize: 24 }}>●</Box>
                  <Typography>Formats</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <ul className="node-list">
                  {NodeDisplayGroup.formats.map((node: SidebarNode) => (
                    <SearchItem
                      key={node.type}
                      title={node.title}
                      type={node.type}
                      bgColor={nodeColorsMap.format}
                    />
                  ))}
                </ul>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ color: nodeColorsMap.task, fontSize: 24 }}>●</Box>
                  <Typography>Tasks</Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <ul className="node-list">
                  {NodeDisplayGroup.tasks.map((node: SidebarNode) => (
                    <SearchItem
                      key={node.type}
                      title={node.title}
                      type={node.type}
                      bgColor={nodeColorsMap.task}
                    />
                  ))}
                </ul>
              </AccordionDetails>
            </Accordion>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Sidebar;
